import { isPlanLimit } from "@rhema/client";
import { UiLink } from "@rhema/ui";
import { useState } from "react";
import type { Platform } from "../platform";
import { useSession } from "../session/session";

/** The Plan section of Settings. */
export const PLAN_PATH = "/you#plan";

/**
 * The text of an error for this app. Plans are bought on the web only, so the iOS and Android
 * apps leave out the sentence that suggests upgrading.
 */
export function errorText(err: unknown, platform: Pick<Platform, "kind">): string {
  const message = err instanceof Error ? err.message : String(err);
  if (platform.kind !== "capacitor") return message;
  return message
    .split(/(?<=\.)\s+/)
    .filter((sentence) => !/upgrade to individual/i.test(sentence))
    .join(" ");
}

/**
 * A link to the Plan section, shown after a plan limit when upgrading would lift it. The iOS and
 * Android apps never show it.
 */
export function UpgradeLink({ className }: { className?: string }) {
  const { platform } = useSession();
  if (platform.kind === "capacitor") return null;
  return (
    <UiLink href={PLAN_PATH} className={className ?? "ml-1 font-semibold text-blue underline"}>
      See plans
    </UiLink>
  );
}

/** An error message, and whether it came from a plan limit the person could lift by upgrading. */
export function useFailure() {
  const { platform } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState(false);
  return {
    error,
    upgrade,
    fail: (err: unknown) => {
      setError(errorText(err, platform));
      setUpgrade(isPlanLimit(err) && err.upgrade);
    },
    clear: () => {
      setError(null);
      setUpgrade(false);
    },
  };
}
